/**
 * MESSAGES ON A TICKET — Young, 2026-10-27 (TSK-0018): "a way for me to ask the responsible person a follow-up question
 * directly within a ticket … notify the responsible person and keep the question and reply in the same ticket history
 * … allow follow-up questions on closed tickets too." Max: like the Messages panel on a Collab Pricing quote — so the
 * same look (CpAttachments.css), with what a ticket needs on top: a file can go with a message (the prices nobody could
 * find), the receiving team answers from the ticket's own link by name, and every message mails the other side.
 *
 * Two doors, one thread (the server half is in server.js, "MESSAGES ON A TICKET"):
 *   - `taskId`  → Ticket Station, signed in. View rights are enough: asking is not editing, so the panel sits outside
 *                 the ticket form's read-only fieldset and works on a closed ticket too.
 *   - `token`   → the ticket's own page, no account; the writer gives their name (the page's PIC field fills it).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, ChevronDown, ChevronUp, Send, Trash2, Loader2, Paperclip, X, Download } from 'lucide-react';
import { BACKEND_URL } from './backendConfig';
import { VN_TZ } from './timeZone';
import './CpAttachments.css';
import './TicketMessages.css';

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = '.pdf,.xlsx,.xls,.csv,.docx,.doc,.pptx,.ppt,.png,.jpg,.jpeg,.zip,.txt';

const fmtWhen = (v) => {
  if (!v) return '';
  const d = new Date(String(v).endsWith('Z') || String(v).includes('+') ? v : `${v}Z`);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', { timeZone: VN_TZ, day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
};
const kb = (n) => (n == null ? '' : n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const readFile = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result || ''));
  r.onerror = () => reject(new Error('The file could not be read.'));
  r.readAsDataURL(file);
});

export default function TicketMessages({ taskId, token, pic = '' }) {
  const { t } = useTranslation();
  const isPublic = !!token;
  const base = isPublic
    ? `${BACKEND_URL}/api/development/public/task/${encodeURIComponent(token)}/messages`
    : `${BACKEND_URL}/api/development/tickets/${encodeURIComponent(taskId || '')}/messages`;
  const fileUrl = (id) => (isPublic ? `${base}/file/${id}` : `${BACKEND_URL}/api/development/photos/file/${id}`);
  const [messages, setMessages] = useState([]);
  const [me, setMe] = useState(0);
  const [draft, setDraft] = useState('');
  const [name, setName] = useState(pic);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(true);
  const fileRef = useRef(null);
  const listRef = useRef(null);
  useEffect(() => { if (pic && !name) setName(pic); }, [pic]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (!taskId && !token) return;
    try {
      const r = await fetch(base, { credentials: 'include' });
      if (!r.ok) return;
      const d = await r.json();
      setMessages(d.messages || []); setMe(d.me || 0);
    } catch (e) { /* the thread is an extra; a failed read must not break the ticket */ }
  }, [base, taskId, token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const el = listRef.current; if (el) el.scrollTop = el.scrollHeight; }, [messages.length, open]);

  const pick = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > MAX_BYTES) { setError(t('xTicketMsg.tooBig', { mb: (f.size / 1048576).toFixed(1) })); return; }
    setError(''); setFile(f);
  };
  const post = async () => {
    const body = draft.trim();
    if (!body || busy) return;
    if (isPublic && !name.trim()) { setError(t('xTicketMsg.needName')); return; }
    setBusy(true); setError(''); setNote('');
    try {
      const payload = { body, ...(isPublic ? { pic: name.trim() } : {}) };
      if (file) payload.file = { fileName: file.name, dataBase64: await readFile(file) };
      const r = await fetch(base, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.error || t('xTicketMsg.sendFailed')); return; }
      setMessages(d.messages || []); if (d.me) setMe(d.me);
      setDraft(''); setFile(null);
      setNote(d.mailed ? t('xTicketMsg.mailed', { to: d.mailed }) : t('xTicketMsg.savedNoMail'));
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const remove = async (m) => {
    if (busy || !window.confirm(t('xTicketMsg.deleteConfirm'))) return;
    setBusy(true); setError('');
    try {
      const r = await fetch(`${base}/${m.msgPk}`, { method: 'DELETE', credentials: 'include' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.error || t('xTicketMsg.deleteFailed')); return; }
      setMessages(d.messages || []);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  if (!taskId && !token) return null;
  return (
    <section className={`cpa-panel tkm ${open ? 'on' : ''}`} data-tkm>
      <button type="button" className="cpa-head" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <MessageSquare size={14} />
        <strong>{t('xTicketMsg.title')}</strong>
        <span className="cpa-n">{messages.length}</span>
        <em className="tkm-sub">{t('xTicketMsg.sub')}</em>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="cpa-body">
          {!!error && <p className="cpa-err">{error}</p>}
          {!messages.length && <p className="cpa-empty">{t('xTicketMsg.empty')}</p>}
          <ul className="cpa-list tkm-list" ref={listRef}>
            {messages.map((m) => (
              <li key={m.msgPk} className={m.kind === 'team' ? 'tkm-team' : ''} data-tkm-msg>
                <div className="cpa-meta">
                  <b>{m.author}</b>
                  {m.kind === 'team' && <span className="tkm-badge">{t('xTicketMsg.team')}</span>}
                  <span>{fmtWhen(m.createdAt)}</span>
                  {!isPublic && m.kind === 'user' && Number(m.userId || 0) === Number(me || 0) && (
                    <button type="button" className="cpa-del" disabled={busy} onClick={() => remove(m)}><Trash2 size={11} /> {t('xTicketMsg.delete')}</button>
                  )}
                </div>
                <p>{m.body}</p>
                {m.file && (
                  <a className="tkm-file" href={fileUrl(m.file.fileId)} target="_blank" rel="noopener noreferrer" data-tkm-file>
                    <Download size={12} /> {m.file.name}{m.file.bytes ? <em> · {kb(m.file.bytes)}</em> : null}
                  </a>
                )}
              </li>
            ))}
          </ul>
          {isPublic && (
            <label className="tkm-name">{t('xTicketMsg.yourName')}
              <input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} placeholder={t('xTicketMsg.yourNamePh')} data-tkm-name />
            </label>
          )}
          <div className="cpa-write">
            <textarea rows={2} value={draft} placeholder={t('xTicketMsg.placeholder')} onChange={(e) => setDraft(e.target.value)} data-tkm-input
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); post(); } }} />
            <div className="tkm-btns">
              <input ref={fileRef} type="file" accept={ACCEPT} hidden onChange={pick} data-tkm-filein />
              <button type="button" className="sp-btn tkm-attach" onClick={() => fileRef.current?.click()} disabled={busy} title={t('xTicketMsg.attach')} data-tkm-attach>
                <Paperclip size={13} /> {t('xTicketMsg.attach')}
              </button>
              <button type="button" className="sp-btn primary" disabled={busy || !draft.trim()} onClick={post} data-tkm-send>
                {busy ? <Loader2 size={13} className="cpa-spin" /> : <Send size={13} />} {t('xTicketMsg.send')}
              </button>
            </div>
          </div>
          {file && (
            <div className="tkm-picked" data-tkm-picked>
              <Paperclip size={12} /> {file.name} <em>· {kb(file.size)}</em>
              <button type="button" className="cpa-del" onClick={() => setFile(null)} aria-label={t('xTicketMsg.removeFile')}><X size={12} /></button>
            </div>
          )}
          <p className="tkm-hint">{note || t('xTicketMsg.hint')}</p>
        </div>
      )}
    </section>
  );
}
